import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import {
  GetUserDocument,
  RemoveUsersFromGroupDocument,
  GetUserQuery,
  User,
  UserGroup,
  RemoveUsersFromGroupMutation,
} from "../../../generated/graphql";
import {
  Box,
  Container,
  Grid,
  Typography,
  List,
  ListItem,
  IconButton,
  Paper,
  ListItemText,
  Backdrop,
  CircularProgress,
} from "@material-ui/core";

import { ButtonPlus, SectionTitle } from "../../shared/Style/Style";
import {
  FlexBetween,
  ListCardClubsGroups,
  PersonalData,
  UserSalaryLink,
  UserSalaryButton,
} from "./UserEdit.style";
import { UserEditForm } from "./Fields";
import { USER_ROLE_MAP } from "../../shared/Type/User.type";
import { useStyles } from "../../Login";
import DeleteIcon from "@material-ui/icons/Delete";

import { ErrorSnackbar } from "../../shared/Error/ErrorSnackbar";
import { GroupModal } from "../../GroupsModal";
import { ErrorMessageSnackbar } from "../../shared/ErrorMessageSnackbar";
import { UserInfoContext } from "../Main";

export const UserEdit = () => {
  const { userId } = useParams<{ userId: string }>();
  const [isGroupsModal, setGroupsModal] = useState<boolean>(false);
  const [GroupsModalRole, setGroupsModalRole] = useState<string>("");

  const [requestError, setRequestError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [disabledOnLoading, setDisabledOnLoading] = useState(false);
  const [userRoleId, setUserRoleId] = useState(0);

  const isUserCanManagePermissions = useContext(
    UserInfoContext
  )?.userPermissions?.includes("CAN_MANAGE_PERMISSIONS");

  const {
    data,
    loading: getUserLoading,
    error: getUserError,
  } = useQuery<GetUserQuery>(GetUserDocument, {
    variables: {
      userId,
    },
  });

  const [
    removeUsersFromGroup,
    {
      data: removeUsersFromGroupData,
      loading: removeUsersFromGroupLoading,
      error: removeUsersFromGroupError,
    },
  ] = useMutation<RemoveUsersFromGroupMutation>(RemoveUsersFromGroupDocument, {
    refetchQueries: [
      {
        query: GetUserDocument,
        variables: { userId },
      },
    ],
  });

  const classes = useStyles();
  const user = data?.getUser as User;
  const clientRole = data?.getUser?.role?.id === 3;
  const staffRole = data?.getUser?.role?.id !== 3;

  // #region effects

  useEffect(() => {
    if (removeUsersFromGroupLoading) {
      setDisabledOnLoading(true);
    }
  }, [removeUsersFromGroupLoading]);

  useEffect(() => {
    if (removeUsersFromGroupError) {
      setDisabledOnLoading(false);
      setRequestError(true);
    }
  }, [removeUsersFromGroupError]);

  useEffect(() => {
    if (removeUsersFromGroupData) {
      setDisabledOnLoading(false);
      setRequestError(false);
    }
  }, [removeUsersFromGroupData]);

  useEffect(() => {
    if (removeUsersFromGroupError) {
      setErrorMessage("При удаленеии пользователя из группы произошла ошибка");
    }
  }, [removeUsersFromGroupError]);

  // #endregion

  if (getUserLoading) {
    return (
      <Backdrop className={classes.backdrop} open={getUserLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <>
      <SectionTitle>
        {clientRole
          ? `Редактирование ${USER_ROLE_MAP.CLIENT.Title}а`
          : `Редактирование ${USER_ROLE_MAP.STAFF.Title}а`}
      </SectionTitle>

      <Container maxWidth="lg" style={{ position: "relative" }}>
        {staffRole && userRoleId !== 3 && (
          <Box display="flex" justifyContent="flex-end">
            <UserSalaryButton>
              <UserSalaryLink to={`/user/${userId}/salaryHistory`}>
                история зарплат
              </UserSalaryLink>
            </UserSalaryButton>

            {isUserCanManagePermissions && (
              <UserSalaryButton>
                <UserSalaryLink to={`/user/${userId}/permissions`}>
                  Разрешения пользователя
                </UserSalaryLink>
              </UserSalaryButton>
            )}
          </Box>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper style={{ padding: "16px" }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography
                    component={PersonalData}
                    variant="subtitle1"
                    gutterBottom
                  >
                    Персональные данные
                  </Typography>

                  <UserEditForm user={user} setUserRoleId={setUserRoleId} />
                </Grid>

                <Grid item xs={12} md={6} style={{ marginBottom: "75px" }}>
                  {!clientRole && userRoleId !== 3 && (
                    <>
                      <FlexBetween style={{ marginTop: "20px" }}>
                        <Box>
                          <Grid container>
                            <Typography
                              component={PersonalData}
                              variant="subtitle1"
                              gutterBottom
                            >
                              Группы
                            </Typography>
                            <ButtonPlus
                              width={250}
                              variant="contained"
                              onClick={() => {
                                setGroupsModalRole("add");
                                setGroupsModal(true);
                              }}
                            >
                              Добавить группу
                            </ButtonPlus>
                          </Grid>

                          <ListCardClubsGroups>
                            <List
                              component="nav"
                              aria-label="secondary mailbox folders"
                            >
                              {user?.groups?.map((group: UserGroup | null) => (
                                <Grid item md={4} xs={6} key={group?.id}>
                                  <ListItem
                                    button
                                    style={{
                                      borderBottom: "1px solid #eee",
                                      marginBottom: "0.5rem",
                                    }}
                                  >
                                    <ListItemText primary={`${group?.name}`} />
                                    <IconButton
                                      disabled={disabledOnLoading}
                                      color="secondary"
                                      aria-label="delete"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeUsersFromGroup({
                                          variables: {
                                            userIds: [userId],
                                            userGroupId: group?.id,
                                          },
                                        });
                                      }}
                                    >
                                      <DeleteIcon fontSize={"small"} />
                                    </IconButton>
                                  </ListItem>
                                </Grid>
                              ))}

                              {(getUserError || removeUsersFromGroupError) && (
                                <ErrorSnackbar
                                  error={
                                    getUserError || removeUsersFromGroupError
                                  }
                                />
                              )}
                            </List>
                          </ListCardClubsGroups>
                        </Box>
                      </FlexBetween>

                      <>
                        {isGroupsModal && (
                          <GroupModal
                            role={GroupsModalRole}
                            setGroupsModalRole={setGroupsModalRole}
                            setGroupsModal={setGroupsModal}
                            open={isGroupsModal}
                            user={user}
                          />
                        )}
                      </>

                      <ErrorMessageSnackbar
                        open={requestError}
                        closeHandler={() => setRequestError(false)}
                        message={errorMessage}
                      />
                    </>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};
